/**
 * Bobo, ready to drop into any screen: the mascot plus everything that makes him alive — the idle cycle
 * (waves, looks around, ponders, hops, dances, turns his back, dozes off), touch reactions (tap, double tap, tap
 * storm, hold) and an optional speech bubble. Port of the admin `BoboMascot`; same props, same moods.
 *
 * Self-contained (no Redux / i18n / theme): the caller passes the already translated `message`.
 *
 * @module components/bobo/BoboMascot
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import Bobo from './Bobo';
import BoboBubble from './BoboBubble';
import {IDLE_MOODS} from './boboLogic';
import {useBoboIdle, useBoboTouch} from './useBoboBehavior';

/**
 * @param {Object} props Props.
 * @param {number} [props.size] Width of Bobo in dp.
 * @param {(string|null)} [props.mood] 'happy' | 'worried' | 'thinking' | 'celebrate' | 'love' | 'surprised' |
 *   'sleepy' | 'greet'; null = calm.
 * @param {(string|null)} [props.message] Speech bubble text; no bubble without it.
 * @param {(string|null)} [props.title] Small orange title of the bubble.
 * @param {string} [props.layout] 'row' (bubble beside Bobo) or 'stack' (bubble below him, centred).
 * @param {boolean} [props.interactive] Let Bobo react to touches.
 * @param {boolean} [props.idle] Run the idle cycle when the mood allows it.
 * @param {string} [props.label] Accessibility label of Bobo.
 * @param {*} [props.style] Extra style of the wrapper.
 * @returns {JSX.Element} The mascot.
 */
const BoboMascot = ({
    size = 120, mood = null, message = null, title = null, layout = 'row', interactive = true, idle = true,
    label = 'Bobo', style,
}) => {
    const {action, poke} = useBoboIdle({enabled: idle && IDLE_MOODS.includes(mood)});
    const {reaction, bursts, handlers} = useBoboTouch({enabled: interactive, onTouch: poke});
    const figure = (
        <Bobo size={size} mood={mood} action={action} reaction={reaction} bursts={bursts} label={label} />
    );
    const stacked = layout === 'stack';

    return (
        <View style={[stacked ? styles.stack : styles.row, style]}>
            {interactive ? (
                <Pressable accessibilityRole="button" accessibilityLabel={label} hitSlop={8} {...handlers}>
                    {figure}
                </Pressable>
            ) : figure}
            {!!message && <BoboBubble title={title} text={message} placement={stacked ? 'below' : 'side'} />}
        </View>
    );
};

const styles = StyleSheet.create({
    row: {flexDirection: 'row', alignItems: 'flex-end', gap: 14},
    stack: {alignItems: 'center'},
});

export default React.memo(BoboMascot);
